// FAQ content for HighwaysIndustry.com
// Structured by category so we can render category-grouped accordions.
// Each FAQ can optionally include a `cta` (label + href) rendered inline.

export const FEATURED_FAQ = {
  question:
    "Why should we invest in brand awareness rather than just sales marketing?",
  answer:
    "In the highways and infrastructure sector, purchasing decisions are rarely made after seeing a single advert. Contracts are often awarded through long procurement processes involving multiple stakeholders, where reputation, experience and trust are critical. Brand awareness ensures your company is consistently visible, recognised and remembered by the people who influence those decisions. Marketing activities such as articles, videos and social media are the tools we use to build that long-term visibility, helping your business become the company people already know when opportunities arise.",
};

export const FAQ_CATEGORIES = [
  {
    slug: "services-overview",
    label: "Services",
    eyebrow: "What we offer",
    items: [
      {
        q: "What services do you offer?",
        a: "Video (drone, ground, editing, CGI), photography, content writing, social media management, website design, graphic design, industry reach, audience growth and brand development, all built specifically for the UK highways and infrastructure sector.",
        cta: { label: "See all services", href: "/what-we-do" },
      },
      {
        q: "Do I need a package?",
        a: "Not necessarily. Packages deliver the best value and the most consistent visibility, but individual services can be booked when you have a specific need, a launch film, a photoshoot, a bid submission.",
        cta: {
          label: "Book a discovery call to discuss options",
          href: "/positioning-review",
        },
      },
      {
        q: "Can I buy individual services?",
        a: "Yes. Each service page details what's included and roughly what to expect. We're happy to quote against a specific brief without committing you to a longer campaign.",
        cta: { label: "Browse services", href: "/what-we-do" },
      },
      {
        q: "Can you create bespoke campaigns?",
        a: "Absolutely. Every serious campaign we run is shaped around your objectives, launch dates, framework windows, framework wins, recruitment drives, awards. Bespoke is the default, not the exception.",
        cta: { label: "Get in touch", href: "/contact" },
      },
    ],
  },
  {
    slug: "video-visuals",
    label: "Video & Visuals",
    eyebrow: "Quick answers",
    items: [
      {
        q: "Do you offer drone filming?",
        a: "Yes, licensed, insured and experienced on live highways sites, National Highways schemes and rail-adjacent projects.",
        cta: {
          label: "See our Drone Filming service",
          href: "/services/drone-filming",
        },
      },
      {
        q: "Can you film on live highways?",
        a: "Yes, planned around live traffic and cleared with your principal contractor and TM team before mobilisation.",
        cta: {
          label: "See our Drone Filming service",
          href: "/services/drone-filming",
        },
      },
      {
        q: "Do you provide ground filming?",
        a: "Yes, interviews, project documentaries, safety demos, event coverage. Cinema cameras, broadcast audio, delivered UK-wide.",
        cta: {
          label: "See our Ground Filming service",
          href: "/services/ground-filming",
        },
      },
      {
        q: "Do you offer photography?",
        a: "Yes, site, portrait, project and event photography, delivered fully edited and licensed for commercial use.",
        cta: {
          label: "See our Photography service",
          href: "/services/photography",
        },
      },
      {
        q: "Can you edit footage we already have?",
        a: "Yes, bring us your rushes and we'll turn them into films the industry will actually watch. External footage welcome across all formats.",
        cta: {
          label: "See our Editing service",
          href: "/services/editing",
        },
      },
      {
        q: "Can you create CGI animations?",
        a: "Yes, 3D visuals, construction sequences, pre-visualisation and mixed-media films combining CGI with real footage.",
        cta: {
          label: "See our CGI service",
          href: "/services/cgi",
        },
      },
    ],
  },
  {
    slug: "brand-strategy",
    label: "Brand & Strategy",
    eyebrow: "The strategy layer",
    items: [
      {
        q: "What is the difference between branding and marketing?",
        a: "Branding is how your business is perceived. Marketing is how you communicate that brand. HighwaysIndustry.com combines both, using consistent content and industry exposure to build long-term recognition and trust.",
      },
      {
        q: "Do you focus on branding or lead generation?",
        a: "Our primary focus is brand growth. By consistently showcasing your projects, expertise and innovations, we help your business become recognised and trusted across the highways and infrastructure sector. This often leads to enquiries and opportunities, but our goal is to strengthen your market position over time.",
      },
      {
        q: "Why do you recommend a 12-month campaign?",
        a: "Reputation isn't built overnight. Consistent visibility throughout the year keeps your business front of mind with decision-makers and allows you to showcase projects, innovations, events and milestones as they happen.",
        cta: {
          label: "See how a 12-month campaign works",
          href: "/positioning-review",
        },
      },
    ],
  },
  {
    slug: "results-roi",
    label: "Results & ROI",
    eyebrow: "The honest answers",
    items: [
      {
        q: "Can you guarantee sales?",
        a: "No. Most highways contracts involve long procurement cycles, multiple stakeholders and framework agreements. No reputable agency can honestly guarantee sales from a specific article or campaign, and anyone claiming otherwise is misrepresenting how the sector actually buys.",
      },
      {
        q: "How do you measure success?",
        a: "We measure success through brand visibility and engagement, including article readership, website traffic, newsletter reach, social engagement, search visibility, inbound enquiries and the overall growth of your industry's awareness of your business.",
      },
      {
        q: "How do I know it's working?",
        a: "Many clients see increased website traffic, more conversations with customers, stronger recruitment, greater social engagement and increased recognition at industry events. Over time, this helps position their business for future opportunities.",
        cta: {
          label: "Read our client case studies",
          href: "/case-studies",
        },
      },
    ],
  },
  {
    slug: "content",
    label: "Content",
    eyebrow: "How it gets made",
    items: [
      {
        q: "Who writes the articles?",
        a: "Our experienced editorial team writes professionally crafted, SEO-optimised articles based on the information you provide. Every article can be reviewed before publication if required.",
      },
      {
        q: "Can we approve content before it goes live?",
        a: "Yes. We can provide drafts for approval to ensure technical accuracy and that the content reflects your brand.",
      },
      {
        q: "Can you visit our projects?",
        a: "Yes. We regularly attend sites across the UK to produce photography, drone footage and video content, from Cornwall to Glasgow and everywhere in between.",
        cta: {
          label: "See our video services",
          href: "/services/ground-filming",
        },
      },
    ],
  },
  {
    slug: "distribution",
    label: "Distribution",
    eyebrow: "How it gets seen",
    items: [
      {
        q: "Where will our content be published?",
        a: "Depending on your package, content may appear on HighwaysIndustry.com, in our 3× weekly newsletter, across our social media channels and within your own marketing channels.",
        cta: {
          label: "Explore our industry reach",
          href: "/industry-reach",
        },
      },
      {
        q: "Will you share our content on LinkedIn?",
        a: "Yes. We distribute content across our social platforms to maximise industry reach, and our LinkedIn following is exactly the audience of decision-makers, engineers and procurement leads your business needs to reach.",
      },
    ],
  },
  {
    slug: "working-together",
    label: "Working Together",
    eyebrow: "What it's like to work with us",
    items: [
      {
        q: "Can you tailor a package?",
        a: "Absolutely. While we offer structured packages, we can also build bespoke campaigns around your objectives.",
        cta: {
          label: "Book a discovery call to discuss a bespoke package",
          href: "/positioning-review",
        },
      },
      {
        q: "Do we have a dedicated account manager?",
        a: "Yes. Every client has a dedicated point of contact to coordinate campaigns and ensure content is delivered consistently.",
      },
      {
        q: "What happens after we sign up?",
        a: "Once you choose a package, your dedicated account manager will schedule a kick-off call to map out your 12-month content calendar, gather your brand assets, and plan your first publications. Most clients see their first piece of content published within two to three weeks of kick-off.",
      },
    ],
  },
];
